// src/pages/HotelEmailsPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Input,
  Button,
  Space,
  Typography,
  Table,
  Tag,
  Modal,
  Select,
  message,
  Popconfirm,
  Tooltip,
} from "antd";
import {
  ReloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";

import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";

const { Title, Text } = Typography;

const safeInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const statusTag = (s) => {
  const v = String(s ?? "").toLowerCase();
  if (v === "sent" || v === "1") return <Tag color="green">Sent</Tag>;
  if (v === "failed" || v === "2") return <Tag color="red">Failed</Tag>;
  if (v === "queued" || v === "0") return <Tag color="gold">Queued</Tag>;
  return <Tag>{String(s ?? "") || "-"}</Tag>;
};

// Slim / neat table styling (same spirit as your BookingListPage)
const slimTableStyles = `
  .slim-table .ant-table-thead > tr > th {
    padding: 6px 8px !important;
    font-size: 12px !important;
    font-weight: 600;
    background: #fafafa;
    white-space: nowrap;
  }

  .slim-table .ant-table-tbody > tr > td {
    padding: 6px 8px !important;
    font-size: 12px !important;
    vertical-align: top;
  }

  .slim-table .ant-tag {
    margin: 0 !important;
    font-size: 11px !important;
    padding: 0px 6px !important;
    border-radius: 10px !important;
    line-height: 18px;
  }

  .slim-table .ant-btn-sm {
    height: 24px !important;
    padding: 0 8px !important;
    font-size: 12px !important;
    border-radius: 6px !important;
  }

  .slim-table .ant-btn-icon-only.ant-btn-sm {
    width: 26px !important;
    padding: 0 !important;
  }

  .slim-table .ant-space {
    gap: 6px !important;
  }

  .slim-table .ant-table-cell {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .slim-table .ant-table-pagination {
    margin: 12px 0 !important;
  }
`;

const HotelEmailsPage = () => {
  const { authData } = useAuth();

  const hotelId = useMemo(() => {
    const n = safeInt(authData?.hotelId);
    return n ?? null;
  }, [authData?.hotelId]);

  // Inject slim styles (local to this page)
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = slimTableStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // state
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState(undefined);
  const [archived, setArchived] = useState(undefined);

  const [viewOpen, setViewOpen] = useState(false);
  const [activeLog, setActiveLog] = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => (mountedRef.current = false);
  }, []);

  // API
  const api = useMemo(() => {
    return {
      searchLogs: (params) => axiosInstance.get("/EmailLogs", { params }),
      archiveLog: (id) => axiosInstance.post(`/EmailLogs/${id}/archive`),
      deleteLog: (id) => axiosInstance.delete(`/EmailLogs/${id}`),
    };
  }, []);

  const normalizeList = (data) => {
    // supports: array OR { items: [] } OR { data: [] }
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  };

  const loadLogs = useCallback(async () => {
    if (!hotelId) return;

    setLoading(true);
    try {
      const res = await api.searchLogs({
        hotelId,
        q: q || undefined,
        status: status || undefined,
        archived: typeof archived === "boolean" ? archived : undefined,
        take: 200,
      });

      setLogs(normalizeList(res?.data));
    } catch (e) {
      message.error("Failed to load outbox logs.");
      setLogs([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [api, hotelId, q, status, archived]);

  useEffect(() => {
    if (hotelId) loadLogs();
  }, [hotelId, loadLogs]);

  const openLogView = useCallback((row) => {
    setActiveLog(row);
    setViewOpen(true);
  }, []);

  const archiveLog = useCallback(
    async (id) => {
      try {
        await api.archiveLog(id);
        message.success("Archived.");
        await loadLogs();
      } catch {
        message.error("Failed to archive.");
      }
    },
    [api, loadLogs]
  );

  const deleteLog = useCallback(
    async (id) => {
      try {
        await api.deleteLog(id);
        message.success("Deleted.");
        await loadLogs();
      } catch {
        message.error("Failed to delete.");
      }
    },
    [api, loadLogs]
  );

  const columns = useMemo(() => {
    return [
      {
        title: "Date",
        key: "createdUtc",
        width: 160,
        render: (_, r) => {
          const v = r?.createdUtc ?? r?.CreatedUtc ?? r?.created ?? r?.Created;
          return v ? new Date(v).toLocaleString() : "-";
        },
      },
      {
        title: "To",
        key: "toEmail",
        width: 220,
        ellipsis: true,
        render: (_, r) => r?.toEmail ?? r?.ToEmail ?? "-",
      },
      {
        title: "Subject",
        key: "subject",
        width: 260,
        ellipsis: true,
        render: (_, r) => r?.subject ?? r?.Subject ?? "-",
      },
      {
        title: "Status",
        key: "status",
        width: 110,
        render: (_, r) => statusTag(r?.status ?? r?.Status),
      },
      {
        title: "Attempts",
        key: "attemptCount",
        width: 90,
        align: "right",
        render: (_, r) => r?.attemptCount ?? r?.AttemptCount ?? 0,
      },
      {
        title: "Error",
        key: "errorMessage",
        ellipsis: true,
        render: (_, r) => r?.errorMessage ?? r?.ErrorMessage ?? "",
      },
      {
        title: "",
        key: "actions",
        fixed: "right",
        width: 110, // compact + prevents overflow
        render: (_, r) => {
          const id = r?.id ?? r?.Id;
          return (
            <Space size={6}>
              <Tooltip title="View">
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => openLogView(r)}
                />
              </Tooltip>

              <Tooltip title="Archive">
                <Button
                  size="small"
                  icon={<FolderOpenOutlined />}
                  onClick={() => archiveLog(id)}
                />
              </Tooltip>

              <Popconfirm
                title="Delete this log record?"
                description="This deletes the HMS log entry (not the mailbox email)."
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={() => deleteLog(id)}
              >
                <Tooltip title="Delete">
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Tooltip>
              </Popconfirm>
            </Space>
          );
        },
      },
    ];
  }, [archiveLog, deleteLog, openLogView]);

  if (!hotelId) {
    return (
      <Card>
        <Title level={5} style={{ marginBottom: 0 }}>
          Outbox Logs
        </Title>
        <Text type="secondary">
          Missing hotelId in auth state. Ensure authData.hotelId is set after login.
        </Text>
      </Card>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Card bodyStyle={{ padding: 12 }}>
          <Space style={{ width: "100%", justifyContent: "space-between" }} align="start">
            <div>
              <Title level={5} style={{ margin: 0 }}>
                Outbox Logs
              </Title>
              <Text type="secondary">Outbound email history for Hotel #{hotelId}.</Text>
            </div>

            <Button size="small" icon={<ReloadOutlined />} onClick={loadLogs}>
              Refresh
            </Button>
          </Space>
        </Card>

        <Card bodyStyle={{ padding: 12 }}>
          <Space wrap style={{ marginBottom: 10 }}>
            <Input
              size="small"
              style={{ width: 320 }}
              placeholder="Search by To / Subject / Template / MessageId"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              allowClear
            />

            <Select
              size="small"
              style={{ width: 150 }}
              placeholder="Status"
              value={status}
              allowClear
              onChange={setStatus}
              options={[
                { value: "Queued", label: "Queued" },
                { value: "Sent", label: "Sent" },
                { value: "Failed", label: "Failed" },
              ]}
            />

            <Select
              size="small"
              style={{ width: 170 }}
              placeholder="Archived"
              value={archived}
              allowClear
              onChange={setArchived}
              options={[
                { value: true, label: "Archived" },
                { value: false, label: "Not archived" },
              ]}
            />

            <Button size="small" icon={<ReloadOutlined />} onClick={loadLogs}>
              Apply
            </Button>
          </Space>

          <Table
            className="slim-table"
            size="small"
            rowKey={(r) => r?.id ?? r?.Id}
            loading={loading}
            dataSource={logs}
            columns={columns}
            tableLayout="fixed"
            scroll={{ x: 1100 }} // ensures the right fixed actions never overflow the page
            pagination={{ pageSize: 10 }}
          />

          <Modal
            open={viewOpen}
            title="Email Log Details"
            onCancel={() => {
              setViewOpen(false);
              setActiveLog(null);
            }}
            footer={[
              <Button
                key="close"
                size="small"
                onClick={() => {
                  setViewOpen(false);
                  setActiveLog(null);
                }}
              >
                Close
              </Button>,
            ]}
            width={820}
          >
            {activeLog ? (
              <Space direction="vertical" style={{ width: "100%" }} size={6}>
                <Text>
                  <b>To:</b> {activeLog?.toEmail ?? activeLog?.ToEmail ?? "-"}
                </Text>
                <Text>
                  <b>Subject:</b> {activeLog?.subject ?? activeLog?.Subject ?? "-"}
                </Text>
                <Text>
                  <b>Status:</b> {String(activeLog?.status ?? activeLog?.Status ?? "-")}
                </Text>
                <Text>
                  <b>Attempts:</b> {activeLog?.attemptCount ?? activeLog?.AttemptCount ?? 0}
                </Text>
                <Text>
                  <b>Message-Id:</b> {activeLog?.messageId ?? activeLog?.MessageId ?? "-"}
                </Text>
                <Text>
                  <b>Error:</b> {activeLog?.errorMessage ?? activeLog?.ErrorMessage ?? "-"}
                </Text>
              </Space>
            ) : null}
          </Modal>
        </Card>
      </Space>
    </div>
  );
};

export default HotelEmailsPage;
